<template>
  <v-dialog v-model="dialog" max-width="520" persistent>
    <v-card>
      <v-card-title class="text-h5">
        <v-icon color="primary" class="mr-3">mdi-cellphone-arrow-down</v-icon>
        {{ appName }} installieren
      </v-card-title>
      <v-card-text>
        <template v-if="isIos">
          <v-alert v-if="!isIosSafari" type="info" outlined dense>
            Öffnen Sie diese Seite zuerst in Safari. Andere Browser können die Installation auf iPhone und iPad nicht direkt starten.
          </v-alert>
          <p class="body-1">So legen Sie die App auf Ihrem Home-Bildschirm ab:</p>
          <ol class="install-steps">
            <li>Tippen Sie in Safari auf <strong>Teilen</strong> <v-icon small>mdi-export-variant</v-icon>.</li>
            <li>Wählen Sie <strong>Zum Home-Bildschirm</strong>.</li>
            <li>Aktivieren Sie gegebenenfalls <strong>Als Web-App öffnen</strong> und tippen Sie auf <strong>Hinzufügen</strong>.</li>
          </ol>
        </template>
        <template v-else>
          <p class="body-1 mb-3">Installieren Sie {{ appName }} für einen schnellen Start direkt vom Home-Bildschirm.</p>
          <v-alert v-if="!installPrompt" type="info" outlined dense>
            Der Installationsdialog ist noch nicht verfügbar. Öffnen Sie alternativ das Browser-Menü und wählen Sie „App installieren“ oder „Zum Startbildschirm hinzufügen“.
          </v-alert>
        </template>
        <v-alert type="info" outlined dense class="mt-4 mb-2">
          Nach der Installation können Sie Benachrichtigungen aktivieren und wichtige Nachrichten auch bei geschlossener App erhalten.
        </v-alert>
        <v-checkbox v-model="neverAsk" hide-details label="Nicht mehr automatisch anzeigen"></v-checkbox>
      </v-card-text>
      <v-card-actions class="px-6 pb-5">
        <v-spacer></v-spacer>
        <v-btn text :disabled="installing" @click="dismiss">Später</v-btn>
        <v-btn v-if="isIos" color="primary" @click="dismiss">Verstanden</v-btn>
        <v-btn v-else color="primary" :loading="installing" :disabled="!installPrompt" @click="install">
          Jetzt installieren
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const REMIND_LATER_MS = 14 * 24 * 60 * 60 * 1000;
const PROMPT_STATE_VERSION = "2";

export default {
  name: "PwaInstallPrompt",
  props: {
    authenticated: { type: Boolean, default: false },
    appName: { type: String, required: true },
    storageKey: { type: String, required: true },
  },
  data() {
    return {
      dialog: false,
      installPrompt: null,
      installing: false,
      neverAsk: false,
      platform: "other",
    };
  },
  computed: {
    isIos() {
      return this.platform === "ios";
    },
    isIosSafari() {
      const userAgent = navigator.userAgent || "";
      return this.isIos && /Safari/i.test(userAgent) && !/(CriOS|FxiOS|EdgiOS|OPiOS)/i.test(userAgent);
    },
  },
  watch: {
    authenticated: {
      immediate: true,
      handler(value) {
        if (value) this.recordVisitAndMaybePrompt();
      },
    },
  },
  mounted() {
    this.platform = this.detectPlatform();
    this.migratePromptState();
    this.emitAvailability();
    this.$root.$on("open-pwa-install", this.openManually);
    window.addEventListener("beforeinstallprompt", this.handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", this.handleInstalled);
    if (this.authenticated) this.recordVisitAndMaybePrompt();
  },
  beforeDestroy() {
    this.$root.$off("open-pwa-install", this.openManually);
    window.removeEventListener("beforeinstallprompt", this.handleBeforeInstallPrompt);
    window.removeEventListener("appinstalled", this.handleInstalled);
  },
  methods: {
    detectPlatform() {
      const userAgent = navigator.userAgent || "";
      const ipadDesktopMode = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
      if (/iPad|iPhone|iPod/i.test(userAgent) || ipadDesktopMode) return "ios";
      if (/Android/i.test(userAgent)) return "android";
      return "other";
    },
    isStandalone() {
      return window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
    },
    isAvailable() {
      return ["android", "ios"].includes(this.platform) && !this.isStandalone() && this.value("installed") !== "true";
    },
    emitAvailability() {
      const available = this.isAvailable();
      this.$emit("availability-change", available);
      this.$root.$emit("pwa-install-availability", available);
    },
    value(key) {
      try {
        return localStorage.getItem(`${this.storageKey}:${key}`);
      } catch (error) {
        return null;
      }
    },
    saveValue(key, value) {
      try {
        localStorage.setItem(`${this.storageKey}:${key}`, String(value));
      } catch (error) {
        // Installation remains available when browser storage is blocked.
      }
    },
    removeValue(key) {
      try {
        localStorage.removeItem(`${this.storageKey}:${key}`);
      } catch (error) {
        // Installation remains available when browser storage is blocked.
      }
    },
    migratePromptState() {
      if (this.value("state-version") === PROMPT_STATE_VERSION) return;
      this.removeValue("installed");
      this.removeValue("remind-after");
      this.removeValue("visits");
      this.saveValue("state-version", PROMPT_STATE_VERSION);
    },
    automaticPromptAllowed() {
      if (this.value("never") === "true") return false;
      return Number(this.value("remind-after") || 0) <= Date.now();
    },
    recordVisitAndMaybePrompt() {
      if (!this.isAvailable() || !this.automaticPromptAllowed()) return;
      this.dialog = true;
    },
    handleBeforeInstallPrompt(event) {
      if (this.platform !== "android" || this.isStandalone()) return;
      event.preventDefault();
      // A new browser install event is authoritative proof that an older
      // locally remembered installation is no longer present.
      this.removeValue("installed");
      this.installPrompt = event;
      this.emitAvailability();
      if (this.authenticated && this.automaticPromptAllowed()) {
        this.dialog = true;
      }
    },
    openManually() {
      if (!this.isAvailable()) return;
      this.neverAsk = false;
      this.dialog = true;
    },
    dismiss() {
      if (this.neverAsk) {
        this.saveValue("never", "true");
      } else {
        this.saveValue("remind-after", Date.now() + REMIND_LATER_MS);
      }
      this.dialog = false;
    },
    async install() {
      if (!this.installPrompt) return;
      this.installing = true;
      try {
        const result = await this.installPrompt.prompt();
        if (result.outcome === "accepted") {
          this.dialog = false;
        } else {
          this.saveValue("remind-after", Date.now() + REMIND_LATER_MS);
          this.dialog = false;
        }
        this.installPrompt = null;
      } finally {
        this.installing = false;
      }
    },
    handleInstalled() {
      this.saveValue("installed", "true");
      this.dialog = false;
      this.installPrompt = null;
      this.$emit("availability-change", false);
      this.$root.$emit("pwa-install-availability", false);
      this.$root.$emit("pwa-install-completed");
    },
  },
};
</script>

<style scoped>
.install-steps { padding-left: 22px; }
.install-steps li { margin-bottom: 12px; line-height: 1.5; }
</style>
